import React, { memo, useCallback, useEffect } from "react";
import { useForm } from "react-hook-form-7";

import {
  Input,
} from "../wombat-ui";

import {
  Button,
  Modal,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  HStack,
  Spacer
} from '@chakra-ui/react';

import useTranslations from "../../hooks/useTranslations";
import useNotification from "../../hooks/useNotification";
import useConfirm from "../utils/confirm/useConfirm";
import { SkillFragment, useDeleteSkill, useInsertSkill } from '../../graphql/generated/definitions';
import useViewer from "../../hooks/useViewer";

interface EditSkillDialogProps {
  open: boolean;
  skill: SkillFragment;
  onClose: () => void;
  onInsert: (data: SkillFragment) => void;
  onDelete: () => void;
}

const EditSkillDialog: React.FC<EditSkillDialogProps> = ({
  open,
  skill,
  onClose,
  onInsert,
  onDelete,
}) => {
  const t = useTranslations();
  const confirm = useConfirm();
  const { notifySuccess, notifyError } = useNotification();

  const { companyId } = useViewer()
  const {
    handleSubmit,
    register,
    formState: { errors },
    reset,
  } = useForm<{ id: string; name: string }>({});

  // reset the form whenever the open value changes
  // to avoid confusion with older values
  useEffect(reset, [open]);

  const [insertSkill, { loading: insertLoading }] = useInsertSkill();

  const onSubmit = useCallback(
    async (data) => {
      try {
        const result = await insertSkill({
          variables: {
            input: {
              ...data,
              companyId
            }
          },
        });
        notifySuccess({
          title: t(
            `professionSkillLibrary.notification.skill.${
              skill ? "update" : "create"
            }.success`,
            { name: data.name }
          ) as string,
        });
        onInsert(result.data.insertSkill.skill);
      } catch (e) {
        notifyError({
          title: t(
            `professionSkillLibrary.notification.skill.${
              skill ? "update" : "create"
            }.error`
          ) as string,
        });
      }
    },
    [skill, insertSkill, onInsert]
  );

  const [deleteSkill, { loading: deleteLoading }] = useDeleteSkill();

  const handleDelete = useCallback(() => {
    confirm({
      title: t("professionSkillLibrary.dialog.skill.destroyConfirmTitle", {
        name: skill.name,
      }),
      message: t("professionSkillLibrary.dialog.skill.destroyConfirm"),
      buttons: [
        { text: t("forms.actions.cancel") },
        {
          colorScheme: "red",
          id: "delete-skill",
          text: t("forms.actions.delete"),
          onClick: async () => {
            try {
              await deleteSkill({
                variables: {
                  input: { id: skill.id },
                },
              });
              notifySuccess({
                title: t(
                  `professionSkillLibrary.notification.skill.destroy.success`,
                  { name: skill.name }
                ) as string,
              });
              onDelete();
            } catch {
              notifyError({
                title: t(
                  `professionSkillLibrary.notification.skill.destroy.error`
                ) as string,
              });
            }
          },
        },
      ],
    });
  }, [deleteSkill, skill, onDelete]);

  return (
    <Modal isOpen={open} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <form id="skillForm" onSubmit={handleSubmit(onSubmit)}>
          <ModalHeader>
            {
              skill ? t("professionSkillLibrary.dialog.skill.title.update", { name: skill.name }) : t("professionSkillLibrary.dialog.skill.title.create")
            }
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <input
              type="hidden"
              defaultValue={skill?.id}
              {...register("id")}
              id="skillId"
            />
            <Input
              label={t("professionSkillLibrary.nameLabel") as string}
              defaultValue={skill?.name}
              {...register("name")}
              id="skillName"
            />
          </ModalBody>
          <ModalFooter>
            <HStack>
              <Button
                onClick={onClose}
                id="cancelEditSkillButton"
              >
                {t("forms.actions.cancel")}
              </Button>
              
              {
                skill?.canDestroy && (
                  <>
                    <Spacer />
                    <Button
                      onClick={handleDelete}
                      isLoading={deleteLoading}
                      variant="outline"
                      colorScheme="red"
                      id="deleteSkillButton"
                    >
                      {t("forms.actions.delete")}
                    </Button>
                    <Spacer />
                  </>
                )
              }
              <Button
                type="submit"
                isLoading={insertLoading}
                disabled={!!errors.name}
                id="submitEditSkillButton"
                colorScheme="blue"
              >
                {t("forms.actions.save")}
              </Button>

            </HStack>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default memo(EditSkillDialog);
